import React, { useState, useEffect } from 'react';
import { Plus, User, Shield, UserCog, Mail, Check, X, Loader2 } from 'lucide-react';
import { staffAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';

const AdminStaff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff' });

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const response = await staffAPI.getAll();
      setStaff(response.data.staff || []);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.error('Please fill in all fields');
      return;
    }
    setSaving(true);
    try {
      await staffAPI.create(form);
      toast.success('Staff member added');
      fetchStaff();
      setShowModal(false);
      setForm({ name: '', email: '', password: '', role: 'staff' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (member) => {
    try {
      if (member.is_active) {
        await staffAPI.deactivate(member.id);
        toast.success('Staff deactivated');
      } else {
        await staffAPI.activate(member.id);
        toast.success('Staff activated');
      }
      fetchStaff();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-6 pt-12 lg:pt-0 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#4A154B]">Staff Management</h1>
          <p className="text-gray-600">Manage staff accounts and permissions</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="btn-primary" data-testid="add-staff-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Staff
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}
        </div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F5E6D3]/50">
                <tr>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-600">Staff Member</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-600">Email</th>
                  <th className="text-center py-4 px-4 text-sm font-medium text-gray-600">Role</th>
                  <th className="text-center py-4 px-4 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-600">Joined</th>
                  <th className="text-center py-4 px-4 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((member) => (
                  <tr key={member.id} className="border-b border-[#F5E6D3]/50 hover:bg-[#F5E6D3]/20">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${member.role === 'admin' ? 'bg-[#4A154B]' : 'bg-[#C9A689]'}`}>
                          {member.role === 'admin' ? <Shield className="w-5 h-5 text-white" /> : <User className="w-5 h-5 text-white" />}
                        </div>
                        <span className="font-medium text-[#4A154B]">{member.name}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-gray-600">{member.email}</td>
                    <td className="py-4 px-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${member.role === 'admin' ? 'bg-[#4A154B] text-white' : 'bg-[#F5E6D3] text-[#4A154B]'}`}>
                        {member.role}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${member.is_active ? 'bg-[#8FBC8F]/20 text-[#5a8a5a]' : 'bg-gray-200 text-gray-600'}`}>
                        {member.is_active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        {member.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-gray-500 text-sm">
                      {format(new Date(member.created_at), 'MMM d, yyyy')}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStatus(member)}
                        className={member.is_active ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                        data-testid={`toggle-staff-${member.id}`}
                      >
                        {member.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#4A154B]">Add Staff Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Full Name *</label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="John Doe" data-testid="staff-name-input" />
            </div>
            <div>
              <label className="form-label">Email *</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="john@salono.com" data-testid="staff-email-input" />
            </div>
            <div>
              <label className="form-label">Password *</label>
              <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} placeholder="••••••••" data-testid="staff-password-input" />
            </div>
            <div>
              <label className="form-label">Role *</label>
              <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="btn-primary" data-testid="save-staff-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Add Staff
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStaff;
